import React, { useCallback, useEffect, useState } from "react";
import { Button, Spinner } from "reactstrap";
import { useHistory } from "react-router-dom";

import { useToast } from "hooks/toast";
import {
  listLocaisArquivo,
  provisionarLocalArquivo,
  getFiscal,
  semearFiscal,
  getParametros,
  aplicarParametros,
  getCredenciais,
  emitirCredenciais,
} from "services/onboardingApi";

const TIPO_LABEL = {
  1: "NF (XML Nota Fiscal)",
  6: "OC (TXT Ocorrências)",
  18: "FC (Frete Combinado)",
  7: "CT-e (XML)",
  19: "Fatura (TXT)",
};

// Badge por Local Arquivo a partir do `sftp` que o backend confere no Wing FTP
// (stat, sem criar). Antes era um "no banco" fixo — verdadeiro, mas não dizia
// se a pasta existia; a Sinto ficou com linhas gravadas e nenhuma pasta.
const BADGE_SFTP = {
  ok: { cls: "done", txt: "no SFTP", title: "Pasta Origem existe no SFTP" },
  pendente: {
    cls: "pend",
    txt: "SFTP pendente",
    title: "Linha gravada no banco, pasta ainda não criada — clique em Provisionar",
  },
  indisponivel: {
    cls: "lock",
    txt: "SFTP indisponível",
    title: "Não foi possível conferir o SFTP agora",
  },
};
function badgeSftp(local) {
  const b = BADGE_SFTP[local.sftp] || BADGE_SFTP.indisponivel;
  return (
    <span className={`onb-badge ${b.cls}`} title={b.title}>
      {b.txt}
    </span>
  );
}

// Configuração automática (passo 6). Seções entram conforme as histórias:
// Local Arquivo + SFTP (H5) implementada; Parâmetros (H2), Fiscal (H4) e
// Credencial (H6) chegam nas próximas.
// `operacional` (PO1-2897): embarcador já homologado (Status=1). O backend
// devolve 409 em "Aplicar defaults" para esse caso (regra de produto: o padrão
// de parâmetros do onboarding só vale para embarcador pendente), então o botão
// já vem desabilitado com a explicação, e o 409 vira aviso, não erro.
export default function ConfigAutomaticaStep({ idEmbarcador, onChanged, operacional = false }) {
  const history = useHistory();
  const { addToast } = useToast();
  const [locais, setLocais] = useState({ embarcador: [], transportadora: [] });
  const [incluirOC, setIncluirOC] = useState(false);
  const [incluirFC, setIncluirFC] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [provisionando, setProvisionando] = useState(false);
  const [sftpResultado, setSftpResultado] = useState(null);
  const [fiscal, setFiscal] = useState(null);
  const [usaIcms, setUsaIcms] = useState("Não");
  const [semeandoFiscal, setSemeandoFiscal] = useState(false);
  const [parametros, setParametros] = useState({ escritos: [], docsAtivos: {} });
  const [tolerancia, setTolerancia] = useState("0");
  const [aplicandoParams, setAplicandoParams] = useState(false);
  const [credenciais, setCredenciais] = useState(null);
  const [emitindo, setEmitindo] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const data = await listLocaisArquivo(idEmbarcador);
      setLocais(data);
      setIncluirOC((data.embarcador || []).some((l) => l.idTipoInformacao === 6));
      setIncluirFC((data.embarcador || []).some((l) => l.idTipoInformacao === 18));
      const contagens = await getFiscal(idEmbarcador);
      setFiscal(contagens);
      if (contagens.tributosEstaduais > 0) setUsaIcms("Sim");
      const params = await getParametros(idEmbarcador);
      setParametros(params);
      const toleranciaEscrita = (params.escritos || []).find(
        (p) => p.nome === "CTePercentualLimiteSemDivergFrete"
      );
      if (toleranciaEscrita) setTolerancia(toleranciaEscrita.valor);
      const cred = await getCredenciais(idEmbarcador);
      setCredenciais(cred);
    } catch (err) {
      console.error("[ConfigAutomatica] carregar:", err);
    } finally {
      setCarregando(false);
    }
  }, [idEmbarcador]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleProvisionar = useCallback(async () => {
    setProvisionando(true);
    setSftpResultado(null);
    try {
      const tipos = ["NF"];
      if (incluirOC) tipos.push("OC");
      if (incluirFC) tipos.push("FC");
      const resultado = await provisionarLocalArquivo(idEmbarcador, {
        tiposEmbarcador: tipos,
      });
      setSftpResultado(resultado.sftp);
      addToast({
        place: "tr",
        type: resultado.sftp && resultado.sftp.ok === false ? "warning" : "success",
        title:
          resultado.sftp && resultado.sftp.ok === false
            ? "Locais gravados; SFTP com pendência"
            : "Local de arquivo provisionado",
        description:
          resultado.sftp && resultado.sftp.erro
            ? resultado.sftp.erro
            : undefined,
        icon: "tim-icons icon-check-2",
      });
      await carregar();
      onChanged();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        err.message;
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao provisionar local de arquivo",
        description: msg,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setProvisionando(false);
    }
  }, [idEmbarcador, incluirOC, incluirFC, addToast, carregar, onChanged]);

  const handleSemearFiscal = useCallback(async () => {
    setSemeandoFiscal(true);
    try {
      const resultado = await semearFiscal(idEmbarcador, {
        icms: usaIcms === "Sim",
      });
      const feriadosOk = (resultado.feriados || []).filter((f) => f.ok).length;
      const feriadosErro = (resultado.feriados || []).filter((f) => !f.ok);
      addToast({
        place: "tr",
        type: feriadosErro.length ? "warning" : "success",
        title: "Fiscal semeado",
        description: [
          resultado.icms && resultado.icms.success
            ? `ICMS: ${resultado.icms.linhas} linhas (${(resultado.icms.ufsOrigem || []).join(", ")})`
            : null,
          `Feriados: ${feriadosOk} ano(s) ok${
            feriadosErro.length ? `, ${feriadosErro.length} com erro` : ""
          }`,
        ]
          .filter(Boolean)
          .join(" · "),
        icon: "tim-icons icon-check-2",
      });
      setFiscal(resultado.contagens);
      onChanged();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        err.message;
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao semear fiscal",
        description: msg,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSemeandoFiscal(false);
    }
  }, [idEmbarcador, usaIcms, addToast, onChanged]);

  const handleAplicarParametros = useCallback(async () => {
    setAplicandoParams(true);
    try {
      const resultado = await aplicarParametros(idEmbarcador, {
        respostas: { 17: tolerancia },
      });
      const partes = [];
      if (resultado.aplicados.length) partes.push(`${resultado.aplicados.length} gravados`);
      if (resultado.atualizados.length) partes.push(`${resultado.atualizados.length} atualizados`);
      if (resultado.jaExistiam.length) partes.push(`${resultado.jaExistiam.length} já existiam`);
      if (resultado.gatesDesligados.length)
        partes.push(
          `${resultado.gatesDesligados.length} gates fora (${resultado.gatesDesligados
            .map((g) => g.documento)
            .join(", ")})`
        );
      addToast({
        place: "tr",
        type: "success",
        title: "Parâmetros aplicados",
        description: partes.join(" · "),
        icon: "tim-icons icon-check-2",
      });
      await carregar();
      onChanged();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        err.message;
      if (err.response && err.response.status === 409) {
        // Embarcador já operacional: regra de produto, não falha. Ajuste
        // pela tela de Parâmetros do cadastro.
        addToast({
          place: "tr",
          type: "warning",
          title: "Embarcador já operacional",
          description: msg,
          icon: "tim-icons icon-alert-circle-exc",
        });
        return;
      }
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao aplicar parâmetros",
        description: msg,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setAplicandoParams(false);
    }
  }, [idEmbarcador, tolerancia, addToast, carregar, onChanged]);

  const handleEmitirCredenciais = useCallback(async () => {
    setEmitindo(true);
    try {
      const resultado = await emitirCredenciais(idEmbarcador);
      addToast({
        place: "tr",
        type: resultado.falhas.length ? "warning" : "success",
        title: `${resultado.emitidas.length} credencial(is) emitida(s)`,
        description: resultado.falhas.length
          ? resultado.falhas.map((f) => `${f.cnpjCpf}: ${f.erro}`).join("\n")
          : undefined,
        icon: "tim-icons icon-key-25",
      });
      setCredenciais(resultado.status);
      onChanged();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        err.message;
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao emitir credenciais",
        description: msg,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setEmitindo(false);
    }
  }, [idEmbarcador, addToast, onChanged]);

  if (carregando) return <Spinner size="sm" />;

  return (
    <div>
      <div className="onb-filial" style={{ alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div className="nm">Pastas do embarcador</div>
          <div className="ci">
            Pastas determinísticas do embarcador (NF, OC, FC — Procedimento
            Importação, Negócio 01). As pastas de CT-e/Fatura por transportadora
            são do onboarding de cada transportadora (PO1-3282), não deste passo.
          </div>
          <div className="d-flex mt-2" style={{ gap: 18 }}>
            <label style={{ fontSize: 13, display: "flex", gap: 6 }}>
              <input type="checkbox" checked disabled /> NF (XML Nota Fiscal)
            </label>
            <label style={{ fontSize: 13, display: "flex", gap: 6 }}>
              <input
                type="checkbox"
                checked={incluirOC}
                onChange={(e) => setIncluirOC(e.target.checked)}
              />{" "}
              OC (TXT Ocorrências)
            </label>
            <label style={{ fontSize: 13, display: "flex", gap: 6 }}>
              <input
                type="checkbox"
                checked={incluirFC}
                onChange={(e) => setIncluirFC(e.target.checked)}
              />{" "}
              FC (Frete Combinado)
            </label>
          </div>
        </div>
        <Button
          color="primary"
          size="sm"
          onClick={handleProvisionar}
          disabled={provisionando}
        >
          {provisionando ? <Spinner size="sm" /> : "Provisionar"}
        </Button>
      </div>

      <div className="onb-filial" style={{ alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div className="nm">Parâmetros</div>
          <div className="ci">
            Padrão canônico (onboarding-parametros.json): 6 automáticos, gates
            por tipo de doc ativo ({["NFE", "CTE", "FATURA"]
              .filter((d) => parametros.docsAtivos && parametros.docsAtivos[d])
              .join(", ") || "nenhum ainda — provisione o Local de Arquivo"}),
            não-listado não é escrito. {(parametros.escritos || []).length}{" "}
            parâmetro(s) gravado(s) hoje.
          </div>
          <div className="d-flex mt-2 align-items-center" style={{ gap: 10 }}>
            <label style={{ fontSize: 13, marginBottom: 0 }}>
              Tolerância CTe × frete sem divergência (%)
            </label>
            <input
              value={tolerancia}
              onChange={(e) => setTolerancia(e.target.value)}
              style={{ width: 70, padding: "4px 8px" }}
              disabled={operacional}
            />
          </div>
          {operacional && (
            <div className="onb-info mt-2" style={{ fontSize: 12 }}>
              Embarcador já operacional: o padrão de parâmetros do onboarding
              só se aplica a embarcador pendente. Ajuste pela tela de
              Parâmetros do cadastro.
            </div>
          )}
        </div>
        <Button
          color="primary"
          size="sm"
          onClick={handleAplicarParametros}
          disabled={aplicandoParams || operacional}
          title={
            operacional
              ? "Embarcador já operacional: ajuste pela tela de Parâmetros do cadastro"
              : undefined
          }
        >
          {aplicandoParams ? <Spinner size="sm" /> : "Aplicar defaults"}
        </Button>
      </div>

      <div className="onb-filial" style={{ alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div className="nm">Fiscal: feriados + tributos</div>
          <div className="ci">
            Feriado nacional sempre semeado (feriadosapi/BrasilAPI, ano atual +
            próximo); estadual/municipal quando o endereço dá UF/IBGE. ICMS pelo
            template padrão carimbado, só estados de origem do cliente. ISS é
            manual (sem template nacional).
          </div>
          <div className="d-flex mt-2 align-items-center" style={{ gap: 10 }}>
            <label style={{ fontSize: 13, marginBottom: 0 }}>
              Este cliente usa ICMS no cálculo?
            </label>
            <select
              value={usaIcms}
              onChange={(e) => setUsaIcms(e.target.value)}
              style={{ padding: "4px 8px" }}
            >
              <option>Não</option>
              <option>Sim</option>
            </select>
            {fiscal && (
              <span className="ci">
                {fiscal.tributosEstaduais} ICMS · {fiscal.tributosMunicipais} ISS ·{" "}
                {fiscal.feriados} feriados
              </span>
            )}
          </div>
        </div>
        <Button
          color="primary"
          size="sm"
          onClick={handleSemearFiscal}
          disabled={semeandoFiscal}
        >
          {semeandoFiscal ? <Spinner size="sm" /> : "Semear fiscal"}
        </Button>
      </div>

      <div className="onb-filial" style={{ alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div className="nm">Credencial de integração</div>
          <div className="ci">
            1 token emitido internamente por remetente cadastrado
            (/api/credenciar via chave interna, PO1-2315).{" "}
            {credenciais
              ? `${credenciais.comToken} de ${credenciais.total} remetente(s) com token.`
              : ""}
          </div>
        </div>
        <Button
          color="primary"
          size="sm"
          onClick={handleEmitirCredenciais}
          disabled={
            emitindo ||
            !credenciais ||
            credenciais.total === 0 ||
            credenciais.comToken === credenciais.total
          }
        >
          {emitindo ? (
            <Spinner size="sm" />
          ) : credenciais && credenciais.total > 0 && credenciais.comToken === credenciais.total ? (
            "Emitidas ✓"
          ) : (
            "Emitir credenciais"
          )}
        </Button>
      </div>

      {sftpResultado && sftpResultado.ok === false && (
        <div className="onb-info">
          <span role="img" aria-label="atenção">⚠️</span>
          <span>
            SFTP pendente: {sftpResultado.erro || "verifique o EverlogAPI"}. As
            linhas do banco foram gravadas; rode o Provisionar de novo para
            re-tentar as pastas.
          </span>
        </div>
      )}

      {(locais.embarcador || []).length > 0 && (
        <div className="mt-3">
          <h5 style={{ fontSize: 14 }}>Locais do embarcador</h5>
          {locais.embarcador.map((local) => (
            <div className="onb-filial" key={`e${local.id}`}>
              <div>
                <div className="nm">
                  {TIPO_LABEL[local.idTipoInformacao] ||
                    `Tipo ${local.idTipoInformacao}`}
                </div>
                <div className="ci">{local.localOrigem}</div>
              </div>
              {badgeSftp(local)}
            </div>
          ))}
        </div>
      )}

      {(locais.transportadora || []).length > 0 && (
        <div className="mt-3">
          <h5 style={{ fontSize: 14 }}>Locais por transportadora (do onboarding de cada uma)</h5>
          {locais.transportadora.map((local) => (
            <div className="onb-filial" key={`t${local.id}`}>
              <div>
                <div className="nm">
                  {local.transportadora} —{" "}
                  {TIPO_LABEL[local.idTipoInformacao] ||
                    `Tipo ${local.idTipoInformacao}`}
                </div>
                <div className="ci">{local.localOrigem}</div>
              </div>
              {badgeSftp(local)}
            </div>
          ))}
        </div>
      )}

      <div className="onb-info">
        <span role="img" aria-label="informação">ℹ️</span>
        <span>
          O <b>usuário Wing FTP</b> do embarcador (login de upload jailed na
          pasta) segue como passo manual — ver subtarefa PO1-2530. As pastas
          são criadas automaticamente no SFTP.
        </span>
      </div>

      {/* PO1-3282 (épico PO1-3279): pastas, usuário Wing FTP e portal de cada
          TRANSPORTADORA são do Onboarding de Transportadora, por vínculo. O botão
          Provisionar acima continua só com as pastas do embarcador. */}
      <div className="mt-2">
        <Button
          color="info"
          outline
          size="sm"
          onClick={() => history.push(`/app/everlog/acesso-sftp-transportadora?embarcadorId=${idEmbarcador}`)}
        >
          Onboarding de Transportadora →
        </Button>
      </div>
    </div>
  );
}
